import ReactMarkdown from 'react-markdown';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

import { AuthenticatedImage, parseUploadUrl } from '@/components/common/authenticated-image';
import { cn } from '@/lib/utils';

const schema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    img: [...(defaultSchema.attributes?.img ?? []), ['src'], ['alt'], ['title']],
    a: [...(defaultSchema.attributes?.a ?? []), ['href'], ['title'], ['target'], ['rel']],
  },
  tagNames: (defaultSchema.tagNames ?? []).filter((name) => name !== 'script' && name !== 'style'),
};

interface MarkdownProps {
  source: string | null | undefined;
  className?: string;
  emptyPlaceholder?: React.ReactNode;
}

export function Markdown({ source, className, emptyPlaceholder }: MarkdownProps) {
  const clean = source?.trim() ?? '';
  if (!clean) {
    return (
      <div className={cn('text-sm italic text-muted-foreground', className)}>
        {emptyPlaceholder ?? 'No content.'}
      </div>
    );
  }
  return (
    <div
      className={cn(
        'prose prose-sm max-w-none text-sm leading-relaxed prose-pre:bg-muted/60 prose-code:font-mono prose-code:text-[13px] dark:prose-invert',
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeSanitize, schema]]}
        skipHtml
        components={{
          a({ children, href }) {
            return (
              <a href={href} target="_blank" rel="noopener noreferrer nofollow">
                {children}
              </a>
            );
          },
          img({ src, alt }) {
            const upload = parseUploadUrl(src ?? undefined);
            if (upload) {
              return (
                <AuthenticatedImage
                  attachmentId={upload.id}
                  {...(upload.variant ? { variant: upload.variant } : {})}
                  alt={alt ?? ''}
                  className="max-h-96 rounded-md border"
                />
              );
            }
            return (
              <img src={src ?? undefined} alt={alt ?? ''} className="max-h-96 rounded-md border" />
            );
          },
        }}
      >
        {clean}
      </ReactMarkdown>
    </div>
  );
}
